import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../../utils/api';
import { LoadingSpinner } from '../ui/Icons';
import Confetti from 'react-confetti';
import { GraduationCap, Timer, Trophy, Users, Play, UserPlus, Zap } from 'lucide-react';

import QuizLayout from './shared/QuizLayout';
import QuizHeader from './shared/QuizHeader';

const SpeedRecognitionQuiz = ({ deck, onFinish, onReview, trackInteraction, userPlantData, onAbort, plants, theme }) => {
    const [gameState, setGameState] = useState('setup'); // 'setup', 'playing', 'finished'
    const [players, setPlayers] = useState([]);
    const [playerNames, setPlayerNames] = useState(['', '', '', '']);
    const [numPlayers, setNumPlayers] = useState(2);
    const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imageOptions, setImageOptions] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [results, setResults] = useState([]);
    const [timeLeft, setTimeLeft] = useState(15);
    const [showNextPlayer, setShowNextPlayer] = useState(false);

    const currentCard = deck[currentIndex];
    const currentPlayer = players[currentPlayerIndex];

    const handleStartGame = () => {
        const validPlayers = playerNames.slice(0, numPlayers).filter(name => name.trim() !== '');
        if (validPlayers.length < 1) return alert('Bitte gib mindestens einen Spielernamen ein!');
        setPlayers(validPlayers.map(name => ({ name: name.trim(), score: 0 })));
        setGameState('playing');
        setCurrentPlayerIndex(0);
        setCurrentIndex(0);
    };

    useEffect(() => {
        if (gameState !== 'playing' || !currentCard || !plants) return;
        const otherPlants = plants.filter(p => p.id !== currentCard.id && p.images?.length > 0)
            .sort(() => 0.5 - Math.random()).slice(0, 2);

        const allOptions = [
            { plant: currentCard, isCorrect: true },
            ...otherPlants.map(p => ({ plant: p, isCorrect: false }))
        ].sort(() => 0.5 - Math.random());

        setImageOptions(allOptions);
        setSelectedImage(null);
        setTimeLeft(15);
    }, [currentIndex, currentCard, plants, gameState]);

    useEffect(() => {
        if (gameState !== 'playing' || selectedImage || !currentCard || showNextPlayer) return;
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleAnswer(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [currentIndex, currentCard, selectedImage, gameState, showNextPlayer]);

    const handleAnswer = (option) => {
        if (selectedImage) return;
        const correct = option.isCorrect;
        setSelectedImage(option);

        const updatedPlayers = [...players];
        if (correct) updatedPlayers[currentPlayerIndex].score += 1;
        setPlayers(updatedPlayers);

        const newResult = { plant: currentCard, correct, player: currentPlayer.name };
        const updatedResults = [...results, newResult];
        setResults(updatedResults);

        const timeTaken = 15 - timeLeft;
        if (onReview) onReview(currentCard, correct, null, timeTaken);

        if (trackInteraction) {
            trackInteraction(correct ? 'FLASHCARD_CORRECT' : 'FLASHCARD_INCORRECT', {
                plant_id: currentCard.id,
                player: currentPlayer.name,
                time_left: timeLeft,
                mode: 'speed_recognition'
            });
        }
        setShowNextPlayer(true);

        setTimeout(() => {
            setShowNextPlayer(false);
            if (currentIndex < deck.length - 1) {
                setCurrentPlayerIndex((currentPlayerIndex + 1) % players.length);
                setCurrentIndex(prev => prev + 1);
            } else {
                setGameState('finished');
            }
        }, 2000);
    };

    return (
        <QuizLayout onClose={onAbort} theme={theme} className="flex flex-col overflow-hidden">

            {gameState === 'setup' && (
                <div className="flex-1 flex flex-col items-center justify-center p-4">
                    <div className={`${theme === 'dark' ? 'bg-white/5 border-white/20' : 'bg-white/80 border-slate-200'} backdrop-blur-3xl border rounded-[3rem] p-8 md:p-12 shadow-2xl space-y-10 animate-in fade-in zoom-in-95 duration-700 w-full max-w-xl`}>
                        <div className="text-center">
                            <div className="w-24 h-24 bg-plantlix-500/20 rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                                <Zap className="w-12 h-12 text-plantlix-400 fill-plantlix-400" />
                            </div>
                            <h2 className={`text-4xl md:text-5xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'} mb-3 tracking-tighter italic`}>SPEED-BATTLE</h2>
                            <p className={`text-[10px] ${theme === 'dark' ? 'text-white/40' : 'text-slate-500'} font-black uppercase tracking-[0.4em]`}>Wer erkennt die Flora zuerst?</p>
                        </div>

                        <div className="space-y-8">
                            <div>
                                <div className="flex items-center justify-between mb-4 px-2">
                                    <label className={`text-[10px] font-black uppercase ${theme === 'dark' ? 'text-white/30' : 'text-slate-400'} tracking-[0.2em] flex items-center gap-3`}>
                                        <Users className="w-4 h-4" />
                                        <span>Teams / Spieler</span>
                                    </label>
                                    <span className="text-xs font-black text-plantlix-500 uppercase tracking-widest">{numPlayers} Player</span>
                                </div>
                                <div className={`grid grid-cols-4 gap-2 ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'} p-2 rounded-[1.8rem] border`}>
                                    {[1, 2, 3, 4].map(num => (
                                        <button
                                            key={num}
                                            onClick={() => setNumPlayers(num)}
                                            className={`py-4 rounded-[1.2rem] font-black text-xl transition-all active:scale-90 ${numPlayers === num ? 'bg-plantlix-500 text-white shadow-xl' : 'text-white/30 hover:bg-white/5'}`}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-3 max-h-[25vh] overflow-y-auto px-1 scrollbar-hide py-1">
                                {Array.from({ length: numPlayers }).map((_, idx) => (
                                    <div key={idx} className="relative group">
                                        <input
                                            type="text"
                                            value={playerNames[idx]}
                                            onChange={e => {
                                                const newNames = [...playerNames];
                                                newNames[idx] = e.target.value;
                                                setPlayerNames(newNames);
                                            }}
                                            placeholder={`Name Player ${idx + 1}`}
                                            className={`w-full ${theme === 'dark' ? 'bg-white/5 border-white/10 text-white placeholder-white/10' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-300'} border-2 rounded-[1.5rem] px-6 py-4 focus:bg-white focus:border-plantlix-500 outline-none transition-all font-black text-lg`}
                                        />
                                    </div>
                                ))}
                            </div>

                            <button onClick={handleStartGame} className="w-full py-6 rounded-[2rem] bg-plantlix-500 hover:bg-plantlix-400 text-white font-black text-2xl shadow-2xl shadow-plantlix-500/40 transition-all active:scale-95 flex items-center justify-center gap-4">
                                <Play className="w-8 h-8 fill-current" />
                                START BATTLE
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {gameState === 'playing' && (
                <div className="flex-1 flex flex-col min-h-0">
                    <QuizHeader
                        current={currentIndex + 1}
                        total={deck.length}
                        onClose={onAbort}
                        status={null}
                        theme={theme}
                    />

                    {/* Elite Score Bar */}
                    <div className="absolute top-0 left-0 w-full h-1.5 z-[70] overflow-hidden">
                        <div 
                            className={`h-full transition-all duration-1000 ease-linear ${timeLeft < 5 ? 'bg-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.8)]' : 'bg-plantlix-500 shadow-[0_0_15px_rgba(16,185,129,0.8)]'}`}
                            style={{ width: `${(timeLeft / 15) * 100}%` }}
                        />
                    </div>

                    {showNextPlayer ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in duration-500">
                             <div className={`w-32 h-32 rounded-full flex items-center justify-center mb-8 shadow-2xl ${selectedImage?.isCorrect ? 'bg-plantlix-500 shadow-plantlix-500/40 animate-bounce' : 'bg-rose-500 shadow-rose-500/40 animate-pulse'}`}>
                                <span className="text-6xl">{selectedImage?.isCorrect ? '✓' : '✗'}</span>
                             </div>
                             <h2 className="text-4xl md:text-7xl font-black text-white mb-12 tracking-tighter uppercase italic">
                                {selectedImage?.isCorrect ? 'LEGENDÄR!' : 'NÄCHSTES MAL!'}
                             </h2>
                             {currentIndex < deck.length - 1 && (
                                <div className="p-8 md:p-12 rounded-[3.5rem] bg-white/5 backdrop-blur-3xl border border-white/20 shadow-2xl w-full max-w-sm">
                                    <p className="text-plantlix-400 text-[10px] uppercase font-black tracking-[0.5em] mb-4">Round Next</p>
                                    <p className="text-4xl md:text-6xl font-black text-white italic">{players[(currentPlayerIndex + 1) % players.length].name}</p>
                                </div>
                             )}
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col min-h-0 space-y-3 px-4 md:px-12 pb-4 md:pb-8">
                            {/* Question Area - Large Image Header Consistency */}
                            <div className="flex-1 min-h-0 relative w-full aspect-[21/9] md:aspect-[21/9] bg-slate-900 rounded-[2rem] overflow-hidden shadow-[0_32px_64px_-12px_rgba(0,0,0,0.6)] border border-white/10 group">
                                {/* Blurred Background using the correct plant (spoiler-free) */}
                                <img 
                                    src={getImageUrl(currentCard.images?.[0]?.image_medium || currentCard.images?.[0]?.image)} 
                                    className="w-full h-full object-cover blur-3xl opacity-20 scale-150"
                                    alt=""
                                />
                                
                                {/* Status Overlay */}
                                <div className="absolute top-3 inset-x-3 flex justify-between z-40">
                                     <div className="flex items-center gap-2 px-3 py-1.5 bg-plantlix-500/90 backdrop-blur-xl border border-plantlix-400/50 rounded-xl shadow-lg text-white">
                                        <Users className="w-4 h-4" />
                                        <div className="flex flex-col text-left">
                                            <span className="text-[8px] font-black uppercase tracking-[0.2em] opacity-80 leading-none">Turn</span>
                                            <span className="text-xs font-black tracking-tighter tabular-nums leading-none">{currentPlayer.name}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-3 px-4 py-1.5 bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl">
                                         <Zap className="w-4 h-4 text-plantlix-400 fill-plantlix-400" />
                                         <span className="text-md font-black tracking-tighter tabular-nums text-white">{players[currentPlayerIndex].score}</span>
                                    </div>
                                </div>

                                {/* Question Overlay */}
                                <div className="absolute bottom-3 inset-x-3 p-3 md:p-6 bg-white/5 backdrop-blur-3xl border border-white/20 rounded-[1.5rem] shadow-2xl text-center z-30">
                                    <span className="text-[8px] uppercase tracking-[0.6em] font-black text-plantlix-400 mb-1 block drop-shadow-md">Speed-Battle</span>
                                    <h3 className={`text-xl md:text-3xl font-black text-white leading-tight tracking-tight italic drop-shadow-2xl`}>
                                        Finde: <span className="text-plantlix-400 underline decoration-plantlix-400/30 underline-offset-8">{currentCard.botanical_name}</span>
                                    </h3>
                                </div>
                            </div>

                             {/* Options Grid */}
                             <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 min-h-0 px-2 mt-4">
                                 {imageOptions.map((option, idx) => {
                                    const isDark = theme === 'dark';
                                    const imgUrl = getImageUrl(option.plant.images?.[0]?.image_medium || option.plant.images?.[0]?.image);
                                    let cellState = isDark
                                        ? "border-white/10 hover:border-plantlix-400/50 hover:bg-white/5"
                                        : "border-slate-200 hover:border-plantlix-500/50 hover:bg-white";

                                    if (selectedImage) {
                                        if (option.isCorrect) {
                                            cellState = "border-plantlix-500 border-4 shadow-[0_0_30px_rgba(16,185,129,0.5)] z-10 scale-[1.05]";
                                        } else if (selectedImage.plant?.id === option.plant.id) {
                                            cellState = "border-rose-500 border-4 shadow-[0_0_30px_rgba(239,68,68,0.5)] z-10 scale-[1.05]";
                                        } else {
                                            cellState = "opacity-20 grayscale scale-[0.95]";
                                        }
                                    }

                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => !selectedImage && handleAnswer(option)}
                                            className={`relative rounded-[2rem] overflow-hidden transition-all duration-500 group shadow-xl bg-slate-900 border-2 ${cellState}`}
                                        >
                                            <img src={imgUrl} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt="" />
                                            {selectedImage && option.isCorrect && (
                                                <div className="absolute inset-0 bg-plantlix-500/10 flex items-center justify-center">
                                                    <div className="bg-white text-plantlix-600 w-12 h-12 rounded-full flex items-center justify-center shadow-2xl animate-in zoom-in duration-300">
                                                        <span className="text-2xl font-black">✓</span>
                                                    </div>
                                                </div>
                                            )}
                                        </button>
                                    );
                                 })}
                             </div>

                             {/* Bottom Leaderboard */}
                             <div className="mt-4 flex gap-2 justify-center overflow-x-auto py-1 scrollbar-hide">
                                 {players.map((p, i) => (
                                    <div key={i} className={`
                                        px-4 py-2 rounded-2xl border flex items-center gap-4 transition-all duration-500
                                        ${i === currentPlayerIndex 
                                            ? 'bg-plantlix-500 border-plantlix-400 shadow-[0_0_30px_rgba(16,185,129,0.4)] scale-105 text-white' 
                                            : (theme === 'dark' ? 'bg-white/5 border-white/10 text-white/30' : 'bg-slate-100 border-slate-200 text-slate-400') + ' opacity-50'}
                                    `}>
                                        <div className="flex flex-col shrink-0">
                                            <span className="text-[7px] font-black uppercase tracking-widest opacity-60 leading-none mb-1">Score</span>
                                            <span className="text-sm font-black tabular-nums leading-none">{p.score}</span>
                                        </div>
                                        <div className="min-w-[60px]">
                                            <span className="text-[10px] font-black italic tracking-tight truncate block">{p.name}</span>
                                        </div>
                                    </div>
                                ))}
                             </div>
                        </div>
                    )}
                </div>
            )}

            {gameState === 'finished' && (
                <div className="flex-1 flex flex-col items-center justify-center p-4">
                    <Confetti numberOfPieces={300} recycle={false} style={{ zIndex: 100 }} />
                    <div className="w-full max-w-xl bg-white/5 backdrop-blur-3xl border border-white/20 rounded-[4rem] p-10 md:p-14 shadow-2xl text-center space-y-12 animate-in zoom-in duration-700">
                        <div className="w-32 h-32 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-yellow-400/30">
                            <Trophy className="w-16 h-16 text-yellow-400 fill-yellow-400" />
                        </div>

                        <div>
                            <p className="text-plantlix-400 text-[10px] uppercase font-black tracking-[0.5em] mb-3">Sieger des Tages</p>
                            <h2 className="text-5xl md:text-7xl font-black text-white mb-4 italic tracking-tighter">
                                {[...players].sort((a, b) => b.score - a.score)[0].name}
                            </h2>
                            <div className="inline-block px-8 py-3 bg-plantlix-500 rounded-full text-white font-black text-2xl shadow-2xl shadow-plantlix-500/30">
                                {[...players].sort((a, b) => b.score - a.score)[0].score} PTS
                            </div>
                        </div>

                        <div className="space-y-3 max-h-[25vh] overflow-y-auto px-1 scrollbar-hide">
                            {[...players].sort((a, b) => b.score - a.score).map((p, idx) => (
                                <div key={idx} className={`flex items-center justify-between p-6 rounded-[2.5rem] border transition-all ${idx === 0 ? 'bg-white/10 border-plantlix-500/50 shadow-2xl shadow-plantlix-500/10' : 'bg-white/5 border-white/5 opacity-50'}`}>
                                    <div className="flex items-center gap-6">
                                        <span className="text-4xl font-black italic text-plantlix-400">#{(idx+1)}</span>
                                        <span className="font-black text-white text-2xl">{p.name}</span>
                                    </div>
                                    <span className="font-black text-3xl text-plantlix-400 tabular-nums">{p.score}</span>
                                </div>
                            ))}
                        </div>

                        <button onClick={() => onFinish(results)} className="w-full py-7 rounded-[2.5rem] bg-white text-black font-black text-3xl shadow-2xl hover:bg-white/90 transition-all active:scale-95 italic tracking-tighter">
                            BEENDEN
                        </button>
                    </div>
                </div>
            )}
        </QuizLayout>
    );
};

export default SpeedRecognitionQuiz;
